import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskLine, EASE } from "@/components/Motion";
import { HERO_SLIDES } from "@/data/content";

const Hero = () => {
  const ref = useRef(null);
  const [slide, setSlide] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.22]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    const id = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 5500);
    return () => clearInterval(id);
  }, []);

  return (
    <section id="home" ref={ref} className="relative h-[100svh] min-h-[620px] overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
        {HERO_SLIDES.map((s, i) => (
          <motion.img
            key={s.img}
            src={s.img}
            alt={s.alt}
            className="absolute inset-0 h-full w-full object-cover"
            initial={{ opacity: i === 0 ? 0.6 : 0, scale: 1.18 }}
            animate={{ opacity: slide === i ? 1 : 0, scale: slide === i ? 1.06 : 1.16 }}
            transition={{ opacity: { duration: 1.6, ease: EASE }, scale: { duration: 6, ease: "linear" } }}
            data-testid={`hero-slide-${i}`}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/35 to-ink/80" />
      </motion.div>

      <div className="absolute bottom-8 left-6 lg:left-12 z-20 hidden sm:flex items-center gap-2" data-testid="hero-slide-dots">
        {HERO_SLIDES.map((s, i) => (
          <button
            key={s.img}
            onClick={() => setSlide(i)}
            aria-label={`Show photo ${i + 1}`}
            data-testid={`hero-slide-dot-${i}`}
            className={`h-1.5 rounded-full transition-all duration-500 ${
              slide === i ? "w-8 bg-brass" : "w-3 bg-cream/40 hover:bg-cream/70"
            }`}
          />
        ))}
      </div>

      <motion.div style={{ opacity: fade }} className="relative z-10 h-full flex flex-col justify-end">
        <div className="mx-auto w-full max-w-[1400px] px-6 lg:px-12 pb-28 sm:pb-32">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.5, ease: EASE }}
            className="text-cream/80 text-[11px] sm:text-xs tracking-[0.45em] uppercase mb-6"
            data-testid="hero-overline"
          >
            Fusion Ristorante — Gurugram
          </motion.p>

          <h1 className="font-serif font-light text-cream text-[11.5vw] sm:text-7xl lg:text-[92px] leading-[1.04] tracking-tight max-w-5xl" data-testid="hero-headline">
            <MaskLine delay={0.55}>Where Every Meal</MaskLine>
            <MaskLine delay={0.7}>Becomes a</MaskLine>
            <MaskLine delay={0.85}>
              <span className="italic text-brass">Love Story.</span>
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.25, ease: EASE }}
            className="mt-6 max-w-xl text-cream/85 text-base sm:text-lg font-light leading-relaxed"
            data-testid="hero-subheadline"
          >
            Italian soul, Indian heart — handcrafted with passion and served with amore.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.45, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/reservations"
              data-testid="hero-reserve-btn"
              className="rounded-full bg-wine px-9 py-4 text-sm tracking-[0.14em] uppercase text-cream hover:bg-winedark hover:scale-[1.03] active:scale-95 transition-all duration-300"
            >
              Reserve a Table
            </Link>
            <Link
              to="/menu"
              data-testid="hero-menu-btn"
              className="rounded-full border border-cream/50 px-9 py-4 text-sm tracking-[0.14em] uppercase text-cream hover:bg-cream hover:text-ink transition-colors duration-300"
            >
              Explore Our Menu
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
          className="absolute bottom-8 right-6 lg:right-12 hidden sm:flex flex-col items-center gap-3 text-cream/70"
          data-testid="hero-scroll-indicator"
        >
          <span className="text-[10px] tracking-[0.4em] uppercase [writing-mode:vertical-rl]">Scroll</span>
          <span className="block h-16 w-px overflow-hidden bg-cream/20">
            <span className="scroll-hint-line block h-full w-full bg-cream/80" />
          </span>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
